import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToggleMaintenance } from "@/features/settings/hooks/useSettingsMutations";
import {
  maintenanceValidation,
  type MaintenanceFormValues,
} from "@/validations/settings";

interface MaintenanceModeFormProps {
  maintenanceMode?: boolean;
  maintenanceMessage?: string;
}

export function MaintenanceModeForm({
  maintenanceMode,
  maintenanceMessage,
}: MaintenanceModeFormProps) {
  const toggleMutation = useToggleMaintenance();

  const formMethods = useForm<MaintenanceFormValues>({
    values: {
      enabled: maintenanceMode ?? false,
      message: maintenanceMessage ?? "",
    },
    resolver: zodResolver(maintenanceValidation),
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = formMethods;

  const isPending = isSubmitting || toggleMutation.isPending;

  async function onSubmit(data: MaintenanceFormValues) {
    await toggleMutation.mutateAsync(data);
  }

  return (
    <Form {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          عند التفعيل، سيتم إيقاف عرض محتوى الموقع الإلكتروني العام مؤقتاً
          (503) دون التأثير على لوحة التحكم.
        </p>

        <FormField
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex w-fit items-center gap-3 rounded-lg border border-border p-3">
              <FormLabel className="mt-0">تفعيل وضع الصيانة</FormLabel>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>رسالة الصيانة (اختياري)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="الموقع تحت الصيانة حالياً، يرجى المحاولة لاحقاً."
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isPending}>
          حفظ وضع الصيانة
          {isPending && <Loader className="mr-2" />}
        </Button>
      </form>
    </Form>
  );
}
